import { EllipsisIcon } from "@xola/icons";
import { FormGroup, Label, Switch, ToggleButton, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { produce } from "immer";
import { startCase } from "lodash";
import React, { useEffect, useState } from "react";
import { Experience, PaymentLineItemStatus, PaymentMethod, SellerPaymentMethod } from "types";
import shallow from "zustand/shallow";
import { useSeller, useUser } from "../../hooks/useAccount";
import { isCreditCardRequired } from "../../models/Experience";
import { getAllowedPaymentMethods, getCreditCardPaymentMethod } from "../../models/Payment";
import { isAffiliate } from "../../models/User";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { PaymentMethodIcon } from "../Common/PaymentMethodIcon";
import { getPairedDevice } from "../Input/EmvInput";
import { isGiftPurchaseItem, isMembershipItem } from "@/models/PurchaseItem";
import { isInvoiceEnabled } from "@/stores/globalStore";

interface PaymentMethodProps {
    value?: SellerPaymentMethod;
    isRefund?: boolean;
    isChargeCard?: boolean;
    onChange: (method: SellerPaymentMethod) => void;
    onChargeCard?: (value: boolean) => void;
    maxMethods?: number;
    excludePaymentMethods?: Array<PaymentMethod | ID>;
    includePaymentMethods?: SellerPaymentMethod[];
    showDoNotChargeCardSwitch?: boolean;
    error?: string | null;
    isOtherSellerPurchase?: boolean;
}

export const PaymentMethodButtons = ({
    value,
    isRefund = false,
    isChargeCard,
    maxMethods = 5,
    onChange,
    onChargeCard,
    excludePaymentMethods = [],
    includePaymentMethods = [],
    showDoNotChargeCardSwitch = true,
    error = null,
    isOtherSellerPurchase = false,
}: PaymentMethodProps) => {
    const [max, setMax] = useState(maxMethods);
    const user = useUser();
    const seller = useSeller();
    const isAffiliateUser = isAffiliate(user);
    const isInvoiceAllowed = isInvoiceEnabled();
    const setPaymentStatus = useCreatePurchaseStore((state) => state.actions.setPaymentStatus);
    const experience = useCreatePurchaseStore((state) =>
        state.getActiveItem() ? state.getActiveProduct<Experience>() : null,
    );
    const purchaseAmount = useCreatePurchaseStore((state) => state.amount);
    const shouldCollectCC = experience && isCreditCardRequired(experience);
    const shouldCollectCCForZeroAmount = shouldCollectCC && purchaseAmount === 0;
    if (shouldCollectCCForZeroAmount) {
        showDoNotChargeCardSwitch = false;
        isChargeCard = false;
    }

    useEffect(() => {
        if (shouldCollectCC) {
            setPaymentStatus(purchaseAmount === 0 ? PaymentLineItemStatus.PENDING : PaymentLineItemStatus.PROCESSING);
        }
    }, [shouldCollectCC, purchaseAmount, setPaymentStatus]);

    const showCCPaymentMethodOnly = isOtherSellerPurchase || isAffiliateUser || shouldCollectCCForZeroAmount;
    let methods = showCCPaymentMethodOnly
        ? getCreditCardPaymentMethod()
        : getAllowedPaymentMethods({ seller, isRefund, isInvoiceAllowed, excludePaymentMethods });

    // Add included payment methods (e.g., BNPL methods from purchase) at the beginning
    if (includePaymentMethods.length > 0) {
        const existingMethodIds = new Set(methods.map((m) => m.id));
        const uniqueIncludedMethods = includePaymentMethods.filter((m) => !existingMethodIds.has(m.id));
        methods = [...uniqueIncludedMethods, ...methods];
    }
    const handleMoreClick = () => {
        setMax(methods.length);
    };

    const handleMethodClick = (method: SellerPaymentMethod) => {
        onChange(method);
        setPaymentStatus(PaymentLineItemStatus.COMPLETE);
    };

    const activeMethod = value?.id ?? PaymentMethod.CREDIT_CARD;

    return (
        <FormGroup name={name}>
            <Label className={clsx("whitespace-nowrap", error && "!text-danger")}>
                {error ? error : "Payment Method"}
            </Label>

            <div data-testid="payment-method-buttons" className="flex flex-row flex-wrap gap-2">
                {methods.slice(0, max).map((method) => (
                    <MethodButton
                        key={method.id}
                        method={method}
                        isActive={activeMethod === method.id}
                        onClick={handleMethodClick}
                    />
                ))}

                {max < methods.length ? <MoreButton onClick={handleMoreClick} /> : null}
            </div>
            {value?.id === PaymentMethod.CREDIT_CARD && (
                <CreditCardPaymentOptions
                    isRefund={isRefund}
                    isChargeCard={isChargeCard}
                    showDoNotChargeCardSwitch={showDoNotChargeCardSwitch}
                    onChargeCard={onChargeCard}
                />
            )}
        </FormGroup>
    );
};

interface CreditCardPaymentOptionsProps {
    isRefund: boolean;
    isChargeCard?: boolean;
    onChargeCard?: (isChargeCard: boolean) => void;
    showDoNotChargeCardSwitch: boolean;
}

const CreditCardPaymentOptions = ({
    isRefund = false,
    isChargeCard = false,
    onChargeCard,
    showDoNotChargeCardSwitch,
}: CreditCardPaymentOptionsProps) => {
    const seller = useSeller();
    const user = useUser();
    const [chargeCard, setChargeCard] = useState(isChargeCard);
    const [isManual, setIsEMV] = useCreatePurchaseStore((state) => [!state.isEMV, state.actions.setIsEMV], shallow);
    const setPaymentStatus = useCreatePurchaseStore((state) => state.actions.setPaymentStatus); // Payment status tracks the "Do not charge now" flag

    const isGiftPurchase = useCreatePurchaseStore((state) =>
        state.purchase.items.some((item) => isGiftPurchaseItem(item)),
    );
    const hasMembershipItem = useCreatePurchaseStore((state) =>
        state.purchase.items.some((item) => isMembershipItem(item)),
    );
    const hasDeposit = useCreatePurchaseStore((state) => state.hasDeposit());

    const handleChargeCard = (value: boolean) => {
        if (onChargeCard) {
            onChargeCard(value);
        }

        setChargeCard(value);
        setPaymentStatus(value ? PaymentLineItemStatus.PENDING : PaymentLineItemStatus.COMPLETE);
    };

    useEffect(() => {
        setChargeCard(isChargeCard);
    }, [isChargeCard]);

    const handleManualEntry = (value: boolean) => {
        setIsEMV(!value);
    };

    const paymentIntentMethods = useCreatePurchaseStore(
        (state) => state.paymentLineItem.payment.paymentIntentMethods,
        shallow,
    );

    useEffect(() => {
        if (isManual && paymentIntentMethods?.includes("card_present")) {
            useCreatePurchaseStore.setState(
                produce((draft) => {
                    delete draft.paymentLineItem.payment.paymentIntentMethods;
                }),
            );
        }

        if (!isManual && !paymentIntentMethods?.length) {
            useCreatePurchaseStore.setState(
                produce((draft) => {
                    delete draft.paymentLineItem.payment.card;
                    draft.paymentLineItem.payment.paymentIntentMethods = ["card_present"];
                }),
            );
        }
    }, [isManual]);

    const hasDevice = !!getPairedDevice(seller) || window.flags?.stripe?.simulateTerminal;
    const isDoNotChargeNowVisible =
        isManual &&
        showDoNotChargeCardSwitch &&
        !isRefund &&
        !isAffiliate(user) &&
        !isGiftPurchase &&
        !hasMembershipItem;

    return (
        <>
            {hasDevice && !isRefund && (
                <div className="mt-2">
                    <Switch.Group>
                        <Switch size="small" isChecked={isManual} onChange={handleManualEntry} />
                        <Switch.Label className="ml-2 text-gray-darker" direction="left">
                            Manual Entry
                        </Switch.Label>
                    </Switch.Group>
                </div>
            )}
            {isDoNotChargeNowVisible && (
                <div className="mt-2">
                    <Tooltip content="Not available in a Deposit workflow" disabled={!hasDeposit}>
                        <Switch.Group>
                            <Switch
                                size="small"
                                data-testid="payment-method-buttons-not-charge-card"
                                isChecked={chargeCard}
                                disabled={hasDeposit}
                                onChange={handleChargeCard}
                            />
                            <Switch.Label className="ml-2 text-gray-darker" direction="left">
                                Do Not Charge Card Now
                            </Switch.Label>
                        </Switch.Group>
                    </Tooltip>
                </div>
            )}
        </>
    );
};

interface MethodButtonProps {
    method: SellerPaymentMethod;
    onClick: (method: SellerPaymentMethod) => void;
    isActive?: boolean;
}

const MethodButton = ({ method, onClick, isActive = false }: MethodButtonProps) => {
    const handleClick = () => {
        if (!isActive) {
            onClick(method);
        }
    };

    // TODO: This var is temporary, should just update the button's test ID to have -active flag
    const nameTestId = `payment-method-name-${method.id}` + (isActive ? "-active" : "");
    return (
        <ToggleButton
            size="medium"
            color={isActive ? "primary" : "secondary"}
            isActive={isActive}
            className={`flex-shrink-0 space-x-1 ${isActive && "!bg-primary-lighter"}`}
            data-testid={`payment-method-button-${method.id}`}
            onClick={handleClick}
        >
            <PaymentMethodIcon method={method.id} size="small" className="relative -top-[0.5px]" />
            <span data-testid={nameTestId}>{startCase(method.name.toLowerCase())}</span>
        </ToggleButton>
    );
};

const MoreButton = ({ onClick }: { onClick: (method: string) => void }) => {
    return (
        <Tooltip content="More">
            <ToggleButton
                isActive={false}
                size="medium"
                color="secondary"
                data-testid="more-button"
                onClick={() => onClick("more")}
            >
                <EllipsisIcon size="medium" className="relative top-[-0.5px] inline-block" />
            </ToggleButton>
        </Tooltip>
    );
};
